import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  MaintenanceWindowDto,
  MonitorCheckDto,
  MonitorCheckKind,
  MonitoringOverviewDto,
  MonitoringSubscriptionDto,
  MonitorObservationDto,
  SecurityBlockSeverity,
  SecurityFindingDto,
  SecurityRemediationAction,
  SecurityTargetKind,
} from '@cpm/shared';
import {
  Activity,
  BookOpen,
  CalendarClock,
  CheckCircle2,
  Play,
  Plus,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMe } from '../api/auth.js';
import { api } from '../api/client.js';
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  Spinner,
  Tabs,
  Textarea,
  cx,
} from '../components/ui.js';

type MonitoringTab = 'uptime' | 'security';

function timeLabel(value: string | null): string {
  if (!value) return 'never';
  return new Date(value).toLocaleString();
}

function checkTone(status: MonitorCheckDto['status']): BadgeTone {
  if (status === 'up') return 'green';
  if (status === 'down' || status === 'degraded') return 'red';
  if (status === 'recovering' || status === 'maintenance') return 'yellow';
  if (status === 'suppressed') return 'blue';
  return 'gray';
}

function severityTone(severity: SecurityFindingDto['severity']): BadgeTone {
  if (severity === 'critical' || severity === 'high') return 'red';
  if (severity === 'medium') return 'yellow';
  if (severity === 'low') return 'blue';
  return 'gray';
}

function LatencyHistory({ check }: { check: MonitorCheckDto }) {
  const observations = useQuery({
    queryKey: ['monitoring', 'observations', check.id],
    queryFn: () =>
      api.get<MonitorObservationDto[]>(`/api/monitoring/checks/${check.id}/observations`),
    refetchInterval: 60_000,
  });
  if (observations.isPending) return <Spinner label="Loading latency history…" />;
  if (observations.error) return <ErrorNote error={observations.error} />;
  const points = [...observations.data]
    .reverse()
    .filter((observation) => observation.latencyMs != null)
    .slice(-60);
  if (points.length === 0) {
    return (
      <EmptyState
        title="No latency samples yet"
        description="Run the check or wait for its next scheduled observation."
      />
    );
  }
  const max = Math.max(...points.map((point) => point.latencyMs ?? 0), 1);
  const coordinates = points
    .map((point, index) => {
      const x = points.length === 1 ? 50 : (index / (points.length - 1)) * 100;
      const y = 38 - ((point.latencyMs ?? 0) / max) * 34;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <div className="space-y-3 px-5 py-4">
      <svg
        viewBox="0 0 100 42"
        className="h-36 w-full overflow-visible rounded-lg bg-slate-50 p-2 dark:bg-slate-950"
        role="img"
        aria-label={`Latency history for ${check.name}`}
        preserveAspectRatio="none"
      >
        <polyline
          points={coordinates}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
          className="text-brand-500"
        />
      </svg>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-slate-500">
            <tr>
              <th className="pb-2 font-medium">Observed</th>
              <th className="pb-2 font-medium">Result</th>
              <th className="pb-2 text-right font-medium">Latency</th>
              <th className="pb-2 text-right font-medium">HTTP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {[...observations.data].slice(0, 12).map((observation) => (
              <tr key={observation.id}>
                <td className="py-2">{timeLabel(observation.observedAt)}</td>
                <td className="py-2">
                  <Badge
                    tone={
                      observation.status === 'success'
                        ? 'green'
                        : observation.status === 'failure'
                          ? 'red'
                          : 'blue'
                    }
                  >
                    {observation.status}
                  </Badge>
                </td>
                <td className="py-2 text-right">
                  {observation.latencyMs == null ? '—' : `${observation.latencyMs} ms`}
                </td>
                <td className="py-2 text-right">{observation.responseStatus ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CheckForm({
  subscriptionId,
  checks,
  onDone,
  initial,
  customerZones,
}: {
  initial?: MonitorCheckDto;
  customerZones?: string[];
  subscriptionId: string;
  checks: MonitorCheckDto[];
  onDone: () => void;
}) {
  const [kind, setKind] = useState<MonitorCheckKind>(initial?.kind ?? 'http');
  const [name, setName] = useState(initial?.name ?? '');
  const [target, setTarget] = useState(initial?.target ?? '');
  const [port, setPort] = useState(String(initial?.port ?? 443));
  const [statuses, setStatuses] = useState(initial?.expectedStatuses.join(', ') ?? '200');
  const [body, setBody] = useState(initial?.expectedBodyContains ?? '');
  const [dependencyCheckId, setDependencyCheckId] = useState(initial?.dependencyCheckId ?? '');
  const [intervalSeconds, setIntervalSeconds] = useState(String(initial?.intervalSeconds ?? 300));
  const [timeoutMs, setTimeoutMs] = useState(String(initial?.timeoutMs ?? 5000));
  const [failureThreshold, setFailureThreshold] = useState(String(initial?.failureThreshold ?? 3));
  const [recoveryThreshold, setRecoveryThreshold] = useState(String(initial?.recoveryThreshold ?? 2));
  const [certificateWarnDays, setCertificateWarnDays] = useState(String(initial?.certificateWarnDays ?? 30));
  const [escalateAfterMinutes, setEscalateAfterMinutes] = useState(String(initial?.escalateAfterMinutes ?? 30));
  const create = useMutation({
    mutationFn: () => {
      const input = {
        name,
        kind,
        target,
        port: kind === 'tcp' ? Number(port) : null,
        expectedStatuses: statuses
          .split(',')
          .map((value) => Number(value.trim()))
          .filter(Number.isFinite),
        expectedBodyContains: kind === 'http' && body.trim() ? body : null,
        intervalSeconds: Number(intervalSeconds),
        timeoutMs: Number(timeoutMs),
        failureThreshold: Number(failureThreshold),
        recoveryThreshold: Number(recoveryThreshold),
        certificateWarnDays: Number(certificateWarnDays),
        escalateAfterMinutes: Number(escalateAfterMinutes),
        dependencyCheckId: dependencyCheckId || null,
      };
      return initial ? api.patch(`/api/monitoring/checks/${initial.id}`, input) : api.post('/api/monitoring/checks', { ...input, subscriptionId });
    },
    onSuccess: onDone,
  });
  return (
    <form
      className="grid gap-4 px-5 py-4 md:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate();
      }}
    >
      {customerZones && <p className="break-words text-sm md:col-span-2">Use public HTTP/HTTPS targets in these assigned zones: {customerZones.join(', ') || 'none — contact your provider'}. Only standard web ports are available.</p>}
      <div>
        <Label htmlFor="monitor-name">Check name</Label>
        <Input
          id="monitor-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          placeholder="Public web health"
        />
      </div>
      <div>
        <Label htmlFor="monitor-kind">Check type</Label>
        <Select
          id="monitor-kind"
          value={kind}
          onChange={(event) => setKind(event.target.value as MonitorCheckKind)}
        >
          <option value="http">HTTP / HTTPS</option>
          {!customerZones && <option value="tcp">TCP port</option>}
          <option value="certificate">Certificate expiry</option>
        </Select>
      </div>
      <div className={cx(kind !== 'tcp' && 'md:col-span-2')}>
        <Label htmlFor="monitor-target">
          {kind === 'http' ? 'URL' : kind === 'tcp' ? 'Host' : 'TLS host'}
        </Label>
        <Input
          id="monitor-target"
          value={target}
          onChange={(event) => setTarget(event.target.value)}
          required
          placeholder={kind === 'http' ? 'https://example.com/health' : 'example.com'}
        />
      </div>
      {kind === 'tcp' && (
        <div>
          <Label htmlFor="monitor-port">Port</Label>
          <Input
            id="monitor-port"
            type="number"
            min={1}
            max={65535}
            value={port}
            onChange={(event) => setPort(event.target.value)}
            required
          />
        </div>
      )}
      {kind === 'http' && (
        <>
          <div>
            <Label htmlFor="monitor-statuses">Expected HTTP statuses</Label>
            <Input
              id="monitor-statuses"
              value={statuses}
              onChange={(event) => setStatuses(event.target.value)}
              placeholder="200, 204"
            />
          </div>
          <div>
            <Label htmlFor="monitor-body">Response must contain</Label>
            <Input
              id="monitor-body"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="optional text assertion"
            />
          </div>
        </>
      )}
      <div className="md:col-span-2">
        <Label htmlFor="monitor-dependency">Suppress when dependency is down</Label>
        <Select
          id="monitor-dependency"
          value={dependencyCheckId}
          onChange={(event) => setDependencyCheckId(event.target.value)}
        >
          <option value="">No dependency</option>
          {checks.map((check) => (
            <option key={check.id} value={check.id}>
              {check.name}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <Label htmlFor="monitor-interval">Check interval (seconds)</Label>
        <Input
          id="monitor-interval"
          type="number"
          min={60}
          max={86_400}
          value={intervalSeconds}
          onChange={(event) => setIntervalSeconds(event.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="monitor-timeout">Timeout (milliseconds)</Label>
        <Input
          id="monitor-timeout"
          type="number"
          min={250}
          max={30_000}
          value={timeoutMs}
          onChange={(event) => setTimeoutMs(event.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="monitor-failure-threshold">Failures before incident</Label>
        <Input
          id="monitor-failure-threshold"
          type="number"
          min={1}
          max={10}
          value={failureThreshold}
          onChange={(event) => setFailureThreshold(event.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="monitor-recovery-threshold">Successes before recovery</Label>
        <Input
          id="monitor-recovery-threshold"
          type="number"
          min={1}
          max={10}
          value={recoveryThreshold}
          onChange={(event) => setRecoveryThreshold(event.target.value)}
          required
        />
      </div>
      {kind === 'certificate' && (
        <div>
          <Label htmlFor="monitor-cert-warning">Warn before expiry (days)</Label>
          <Input
            id="monitor-cert-warning"
            type="number"
            min={1}
            max={365}
            value={certificateWarnDays}
            onChange={(event) => setCertificateWarnDays(event.target.value)}
            required
          />
        </div>
      )}
      <div>
        <Label htmlFor="monitor-escalation">Escalate open incident after (minutes)</Label>
        <Input
          id="monitor-escalation"
          type="number"
          min={0}
          max={10_080}
          value={escalateAfterMinutes}
          onChange={(event) => setEscalateAfterMinutes(event.target.value)}
          required
        />
      </div>
      {create.error && (
        <div className="md:col-span-2">
          <ErrorNote error={create.error} />
        </div>
      )}
      <div className="md:col-span-2 flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onDone}>Cancel edit</Button>
        <Button type="submit" loading={create.isPending}>
          <Plus className="size-4" /> {initial ? 'Save check' : 'Add check'}
        </Button>
      </div>
    </form>
  );
}

function MaintenanceForm({
  subscriptionId,
  checks,
  onDone,
}: {
  subscriptionId: string;
  checks: MonitorCheckDto[];
  onDone: () => void;
}) {
  const [checkId, setCheckId] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [reason, setReason] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api.post('/api/monitoring/maintenance', {
        subscriptionId,
        checkId: checkId || null,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        reason,
      }),
    onSuccess: onDone,
  });
  return (
    <form
      className="grid gap-4 px-5 py-4 md:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate();
      }}
    >
      <div>
        <Label htmlFor="maintenance-check">Scope</Label>
        <Select
          id="maintenance-check"
          value={checkId}
          onChange={(event) => setCheckId(event.target.value)}
        >
          <option value="">All subscription checks</option>
          {checks.map((check) => (
            <option key={check.id} value={check.id}>
              {check.name}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <Label htmlFor="maintenance-reason">Reason</Label>
        <Input
          id="maintenance-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          required
          placeholder="Scheduled database upgrade"
        />
      </div>
      <div>
        <Label htmlFor="maintenance-start">Starts</Label>
        <Input
          id="maintenance-start"
          type="datetime-local"
          value={startsAt}
          onChange={(event) => setStartsAt(event.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="maintenance-end">Ends</Label>
        <Input
          id="maintenance-end"
          type="datetime-local"
          value={endsAt}
          onChange={(event) => setEndsAt(event.target.value)}
          required
        />
      </div>
      {create.error && (
        <div className="md:col-span-2">
          <ErrorNote error={create.error} />
        </div>
      )}
      <div className="md:col-span-2 flex justify-end">
        <Button type="submit" loading={create.isPending}>
          <CalendarClock className="size-4" /> Schedule window
        </Button>
      </div>
    </form>
  );
}

function UptimePanel({
  data,
  refresh,
  selectedCheckId,
}: {
  data: MonitoringSubscriptionDto;
  refresh: () => void;
  selectedCheckId?: string | null;
}) {
  const [showCheckForm, setShowCheckForm] = useState(false);
  const [editingCheck, setEditingCheck] = useState<MonitorCheckDto | undefined>();
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false);
  const [historyCheckId, setHistoryCheckId] = useState<string | null>(null);
  useEffect(() => { setHistoryCheckId(selectedCheckId ?? null); }, [selectedCheckId, data.subscriptionId]);
  const removeCheck = useMutation({
    mutationFn: (id: string) => api.delete(`/api/monitoring/checks/${id}`),
    onSuccess: refresh,
  });
  const runCheck = useMutation({
    mutationFn: (id: string) => api.post(`/api/monitoring/checks/${id}/run`, {}),
    onSuccess: refresh,
  });
  const toggleCheck = useMutation({
    mutationFn: (check: MonitorCheckDto) =>
      api.patch(`/api/monitoring/checks/${check.id}`, { enabled: !check.enabled }),
    onSuccess: refresh,
  });
  const removeWindow = useMutation({
    mutationFn: (id: string) => api.delete(`/api/monitoring/maintenance/${id}`),
    onSuccess: refresh,
  });
  const historyCheck = data.checks.find((check) => check.id === historyCheckId);
  const checkName = new Map(data.checks.map((check) => [check.id, check.name]));

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader
          title="Uptime and certificate checks"
          description="Failure and recovery thresholds prevent one bad sample from paging. Dependencies and maintenance windows suppress downstream noise."
          actions={
            data.permissions.manageChecks || data.customerManagement ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => { setEditingCheck(undefined); setShowCheckForm((value) => !value); }}
              >
                <Plus className="size-4" /> Add check
              </Button>
            ) : undefined
          }
        />
        {showCheckForm && (
          <CheckForm
            key={editingCheck?.id ?? 'new'}
            initial={editingCheck}
            customerZones={data.customerManagement?.zones}
            subscriptionId={data.subscriptionId}
            checks={data.checks.filter((check) => check.id !== editingCheck?.id && (!data.customerManagement || data.customerManagement.checkIds.includes(check.id)))}
            onDone={() => {
              setShowCheckForm(false);
              refresh();
            }}
          />
        )}
        {data.checks.length === 0 ? (
          <EmptyState
            icon={<Activity className="size-10" />}
            title="No monitoring checks"
            description="Add an HTTP, TCP, or certificate-expiry check when the plan enables uptime monitoring."
          />
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {data.checks.map((check) => (
              <li key={check.id} className="px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="flex flex-wrap items-center gap-2 font-medium">
                      {check.name}
                      <Badge tone={checkTone(check.status)}>{check.status}</Badge>
                      <Badge tone="gray">{check.kind}</Badge>
                      {!check.enabled && <Badge tone="gray">disabled</Badge>}
                    </p>
                    <p className="mt-1 break-all font-mono text-xs text-slate-500">
                      {check.target}
                      {check.port ? `:${check.port}` : ''}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Last checked {timeLabel(check.lastCheckedAt)}
                      {check.dependencyCheckId
                        ? ` · depends on ${checkName.get(check.dependencyCheckId) ?? 'deleted check'}`
                        : ''}
                    </p>
                    {check.lastError && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                        {check.lastError}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() =>
                        setHistoryCheckId((value) => (value === check.id ? null : check.id))
                      }
                    >
                      <Activity className="size-4" /> History
                    </Button>
                    {(data.permissions.manageChecks || data.customerManagement?.checkIds.includes(check.id)) && (
                      <>
                        <Button type="button" variant="secondary" onClick={() => { setEditingCheck(check); setShowCheckForm(true); }}>Edit check</Button>
                        <Button
                          type="button"
                          variant="ghost"
                          loading={toggleCheck.isPending && toggleCheck.variables?.id === check.id}
                          onClick={() => toggleCheck.mutate(check)}
                        >
                          {check.enabled ? 'Disable' : 'Enable'}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          loading={runCheck.isPending && runCheck.variables === check.id}
                          onClick={() => runCheck.mutate(check.id)}
                        >
                          <Play className="size-4" /> Run
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          aria-label={`Delete ${check.name}`}
                          loading={removeCheck.isPending && removeCheck.variables === check.id}
                          onClick={() => {
                            if (window.confirm(`Delete monitoring check "${check.name}"?`)) {
                              removeCheck.mutate(check.id);
                            }
                          }}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                {historyCheck?.id === check.id && <LatencyHistory check={check} />}
              </li>
            ))}
          </ul>
        )}
        {(runCheck.error ?? toggleCheck.error ?? removeCheck.error) && (
          <div className="px-5 pb-4">
            <ErrorNote error={runCheck.error ?? toggleCheck.error ?? removeCheck.error} />
          </div>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Incidents"
          description="A single deduplicated incident records opening, escalation, flapping suppression, and recovery."
        />
        {data.incidents.length === 0 ? (
          <EmptyState title="No incidents" description="Failed checks will appear here." />
        ) : (
          <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {data.incidents.map((incident) => (
              <li key={incident.id} className="px-5 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={incident.status === 'open' ? 'red' : 'green'}>
                    {incident.status}
                  </Badge>
                  {incident.escalatedAt && <Badge tone="red">escalated</Badge>}
                  {incident.flapping && <Badge tone="yellow">flapping</Badge>}
                  <span className="font-medium">
                    {checkName.get(incident.checkId) ?? 'Deleted check'}
                  </span>
                </div>
                <p className="mt-1 text-slate-600 dark:text-slate-300">{incident.summary}</p>
                <p className="mt-1 text-xs text-slate-500">
                  Opened {timeLabel(incident.openedAt)}
                  {incident.resolvedAt ? ` · recovered ${timeLabel(incident.resolvedAt)}` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Maintenance windows"
          description="Suppress incident transitions for one check or the whole subscription while planned work is active."
          actions={
            data.permissions.manageChecks ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowMaintenanceForm((value) => !value)}
              >
                <CalendarClock className="size-4" /> Add window
              </Button>
            ) : undefined
          }
        />
        {showMaintenanceForm && (
          <MaintenanceForm
            subscriptionId={data.subscriptionId}
            checks={data.checks}
            onDone={() => {
              setShowMaintenanceForm(false);
              refresh();
            }}
          />
        )}
        {data.maintenanceWindows.length === 0 ? (
          <EmptyState title="No maintenance windows" />
        ) : (
          <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {data.maintenanceWindows.map((windowRow: MaintenanceWindowDto) => (
              <li
                key={windowRow.id}
                className="flex flex-wrap items-center justify-between gap-3 px-5 py-3"
              >
                <div>
                  <p className="font-medium">{windowRow.reason}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {windowRow.checkId
                      ? checkName.get(windowRow.checkId) ?? 'Deleted check'
                      : 'All checks'}
                    {' · '}
                    {timeLabel(windowRow.startsAt)} – {timeLabel(windowRow.endsAt)}
                  </p>
                </div>
                {data.permissions.manageChecks && (
                  <Button
                    type="button"
                    variant="ghost"
                    aria-label={`Delete maintenance window ${windowRow.reason}`}
                    onClick={() => removeWindow.mutate(windowRow.id)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {removeWindow.error && (
          <div className="px-5 pb-4">
            <ErrorNote error={removeWindow.error} />
          </div>
        )}
      </Card>
    </div>
  );
}

function ScanTargetForm({
  data,
  onDone,
}: {
  data: MonitoringSubscriptionDto;
  onDone: () => void;
}) {
  const [resourceId, setResourceId] = useState(data.resources[0]?.id ?? '');
  const [kind, setKind] = useState<SecurityTargetKind>('image');
  const [target, setTarget] = useState('');
  const [cadenceHours, setCadenceHours] = useState('24');
  const [blockSeverity, setBlockSeverity] = useState<SecurityBlockSeverity>('critical');
  const create = useMutation({
    mutationFn: () =>
      api.post('/api/monitoring/scan-targets', {
        subscriptionId: data.subscriptionId,
        resourceId,
        kind,
        target,
        cadenceHours: Number(cadenceHours),
        blockSeverity,
      }),
    onSuccess: onDone,
  });
  return (
    <form
      className="grid gap-4 px-5 py-4 md:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate();
      }}
    >
      <div>
        <Label htmlFor="scan-resource">Workload</Label>
        <Select
          id="scan-resource"
          value={resourceId}
          onChange={(event) => setResourceId(event.target.value)}
          required
        >
          {data.resources.map((resource) => (
            <option key={resource.id} value={resource.id}>
              {resource.name} ({resource.kind})
            </option>
          ))}
        </Select>
      </div>
      <div>
        <Label htmlFor="scan-kind">Source</Label>
        <Select
          id="scan-kind"
          value={kind}
          onChange={(event) => setKind(event.target.value as SecurityTargetKind)}
        >
          <option value="image">Container image vulnerability scan</option>
          <option value="volume">Selected persistent-volume file scan</option>
        </Select>
      </div>
      <div className="md:col-span-2">
        <Label htmlFor="scan-target">{kind === 'image' ? 'Image reference' : 'Docker volume'}</Label>
        <Input
          id="scan-target"
          value={target}
          onChange={(event) => setTarget(event.target.value)}
          required
          placeholder={kind === 'image' ? 'registry.example/app:release' : 'project_data'}
        />
        <p className="mt-1 text-xs text-slate-500">
          {kind === 'image'
            ? 'Image scans can block a deployment at the chosen severity.'
            : 'The volume is mounted read-only. Scanning never moves, deletes, or rewrites files.'}
        </p>
      </div>
      <div>
        <Label htmlFor="scan-cadence">Cadence (hours)</Label>
        <Input
          id="scan-cadence"
          type="number"
          min={1}
          max={720}
          value={cadenceHours}
          onChange={(event) => setCadenceHours(event.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="scan-block">Block image deployment at</Label>
        <Select
          id="scan-block"
          value={blockSeverity}
          onChange={(event) =>
            setBlockSeverity(event.target.value as SecurityBlockSeverity)
          }
          disabled={kind === 'volume'}
        >
          <option value="critical">Critical</option>
          <option value="high">High or critical</option>
          <option value="none">Never block</option>
        </Select>
      </div>
      {create.error && (
        <div className="md:col-span-2">
          <ErrorNote error={create.error} />
        </div>
      )}
      <div className="md:col-span-2 flex justify-end">
        <Button type="submit" loading={create.isPending} disabled={!resourceId}>
          <Plus className="size-4" /> Add scan target
        </Button>
      </div>
    </form>
  );
}

function SecurityPanel({
  data,
  refresh,
  portal,
}: {
  data: MonitoringSubscriptionDto;
  refresh: () => void;
  portal: boolean;
}) {
  const [showTargetForm, setShowTargetForm] = useState(false);
  const runTarget = useMutation({
    mutationFn: (id: string) => api.post(`/api/monitoring/scan-targets/${id}/run`, {}),
    onSuccess: refresh,
  });
  const removeTarget = useMutation({
    mutationFn: (id: string) => api.delete(`/api/monitoring/scan-targets/${id}`),
    onSuccess: refresh,
  });
  const toggleTarget = useMutation({
    mutationFn: (target: MonitoringSubscriptionDto['scanTargets'][number]) =>
      api.patch(`/api/monitoring/scan-targets/${target.id}`, {
        enabled: !target.enabled,
      }),
    onSuccess: refresh,
  });
  const classify = useMutation({
    mutationFn: ({
      finding,
      status,
    }: {
      finding: SecurityFindingDto;
      status: 'open' | 'false_positive' | 'accepted_risk';
    }) => {
      const reason = window.prompt(`Reason for marking ${finding.sourceId} ${status}:`);
      if (!reason) throw new Error('A classification reason is required');
      return api.post(`/api/monitoring/findings/${finding.id}/classify`, {
        status,
        reason,
        expiresAt:
          status === 'open' ? null : new Date(Date.now() + 30 * 86_400_000).toISOString(),
      });
    },
    onSuccess: refresh,
  });
  const remediate = useMutation({
    mutationFn: ({
      finding,
      action,
    }: {
      finding: SecurityFindingDto;
      action: SecurityRemediationAction;
    }) => {
      const note = window.prompt(
        `Explain the reviewed ${action.replace('_', ' ')} request. No files will be changed by the panel:`,
      );
      if (!note) throw new Error('A remediation note is required');
      return api.post(`/api/monitoring/findings/${finding.id}/remediation`, {
        action,
        note,
      });
    },
    onSuccess: refresh,
  });
  const review = useMutation({
    mutationFn: ({
      id,
      decision,
    }: {
      id: string;
      decision: 'approve' | 'reject';
    }) => {
      const note = window.prompt(
        `${decision === 'approve' ? 'Approval' : 'Rejection'} rationale:`,
      );
      if (!note) throw new Error('A review note is required');
      return api.post(`/api/monitoring/remediation/${id}/review`, { decision, note });
    },
    onSuccess: refresh,
  });
  const latestRun = new Map<string, MonitoringSubscriptionDto['scanRuns'][number]>();
  for (const run of data.scanRuns) {
    if (!latestRun.has(run.targetId)) latestRun.set(run.targetId, run);
  }
  const helpHref = (href: string) => (portal ? href.replace('/help/', '/portal/help/') : href);
  const pendingError =
    runTarget.error ??
    toggleTarget.error ??
    removeTarget.error ??
    classify.error ??
    remediate.error ??
    review.error;

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader
          title="Workload scan targets"
          description="Trivy checks container images before deployment and on schedule. ClamAV scans only selected persistent volumes through read-only mounts."
          actions={
            data.permissions.manageSecurity ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowTargetForm((value) => !value)}
              >
                <Plus className="size-4" /> Add target
              </Button>
            ) : undefined
          }
        />
        {showTargetForm && (
          <ScanTargetForm
            data={data}
            onDone={() => {
              setShowTargetForm(false);
              refresh();
            }}
          />
        )}
        {data.scanTargets.length === 0 ? (
          <EmptyState
            icon={<ShieldAlert className="size-10" />}
            title="No workload scan targets"
            description="Add an image or selected persistent volume when the plan enables workload scanning."
          />
        ) : (
          <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {data.scanTargets.map((target) => {
              const run = latestRun.get(target.id);
              return (
                <li
                  key={target.id}
                  className="flex flex-wrap items-start justify-between gap-3 px-5 py-4"
                >
                  <div>
                    <p className="flex flex-wrap items-center gap-2 font-medium">
                      {target.resourceName}
                      <Badge tone="blue">{target.kind}</Badge>
                      <Badge tone={target.enabled ? 'green' : 'gray'}>
                        {target.enabled ? 'schedule active' : 'schedule paused'}
                      </Badge>
                      {run && (
                        <Badge
                          tone={
                            run.status === 'succeeded'
                              ? 'green'
                              : run.status === 'failed'
                                ? 'red'
                                : 'blue'
                          }
                        >
                          {run.status}
                        </Badge>
                      )}
                    </p>
                    <p className="mt-1 break-all font-mono text-xs text-slate-500">
                      {target.target}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Every {target.cadenceHours}h
                      {target.kind === 'image'
                        ? ` · deployment policy: ${target.blockSeverity}`
                        : ' · read-only file scan'}
                      {target.kind === 'image' && !target.enabled
                        ? ' · deploy admission remains active'
                        : ''}
                      {' · '}last scanned {timeLabel(target.lastScannedAt)}
                    </p>
                    {target.lastError && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                        {target.lastError}
                      </p>
                    )}
                  </div>
                  {data.permissions.manageSecurity && (
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        loading={
                          toggleTarget.isPending && toggleTarget.variables?.id === target.id
                        }
                        onClick={() => toggleTarget.mutate(target)}
                      >
                        {target.enabled ? 'Pause schedule' : 'Resume schedule'}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        loading={runTarget.isPending && runTarget.variables === target.id}
                        onClick={() => runTarget.mutate(target.id)}
                      >
                        <Play className="size-4" /> Scan now
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        aria-label={`Delete scan target ${target.target}`}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Delete scan target "${target.target}"? Findings and run history are retained only until the target is removed.`,
                            )
                          ) {
                            removeTarget.mutate(target.id);
                          }
                        }}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Actionable findings"
          description="Severity is paired with the affected location, available fix, classification state, and a concrete runbook."
        />
        {data.findings.length === 0 ? (
          <EmptyState
            icon={<CheckCircle2 className="size-10" />}
            title="No findings"
            description="A successful scan with no findings is recorded separately from a scanner failure."
          />
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {data.findings.map((finding) => (
              <li key={finding.id} className="px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 font-medium">
                      {finding.title}
                      <Badge tone={severityTone(finding.severity)}>{finding.severity}</Badge>
                      <Badge tone={finding.status === 'open' ? 'red' : 'gray'}>
                        {finding.status.replace('_', ' ')}
                      </Badge>
                    </p>
                    <p className="mt-1 break-all font-mono text-xs text-slate-500">
                      {finding.sourceId}
                      {finding.location ? ` · ${finding.location}` : ''}
                    </p>
                    {(finding.installedVersion || finding.fixedVersion) && (
                      <p className="mt-1 text-xs text-slate-500">
                        Installed {finding.installedVersion ?? 'unknown'} · fixed in{' '}
                        {finding.fixedVersion ?? 'not yet available'}
                      </p>
                    )}
                    {finding.statusReason && (
                      <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">
                        Classification: {finding.statusReason}
                        {finding.statusExpiresAt
                          ? ` · review by ${timeLabel(finding.statusExpiresAt)}`
                          : ''}
                      </p>
                    )}
                    <Link
                      to={helpHref(finding.runbookHref)}
                      className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
                    >
                      <BookOpen className="size-4" /> {finding.runbookLabel}
                    </Link>
                  </div>
                  {data.permissions.manageSecurity && (
                    <div className="flex flex-wrap gap-2">
                      {finding.status === 'open' ? (
                        <>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() =>
                              classify.mutate({ finding, status: 'false_positive' })
                            }
                          >
                            False positive
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() =>
                              classify.mutate({ finding, status: 'accepted_risk' })
                            }
                          >
                            Accept 30 days
                          </Button>
                        </>
                      ) : (
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => classify.mutate({ finding, status: 'open' })}
                        >
                          Reopen
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() =>
                          remediate.mutate({
                            finding,
                            action:
                              finding.kind === 'malware' ? 'quarantine' : 'rebuild_image',
                          })
                        }
                      >
                        Request reviewed action
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Remediation review"
          description="The panel records approval only. It never deletes, quarantines, patches, or rewrites customer data automatically."
        />
        {data.remediation.length === 0 ? (
          <EmptyState title="No remediation requests" />
        ) : (
          <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {data.remediation.map((item) => (
              <li
                key={item.id}
                className="flex flex-wrap items-start justify-between gap-3 px-5 py-4"
              >
                <div>
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    {item.action.replace('_', ' ')}
                    <Badge
                      tone={
                        item.status === 'approved'
                          ? 'green'
                          : item.status === 'rejected'
                            ? 'red'
                            : 'yellow'
                      }
                    >
                      {item.status}
                    </Badge>
                    <Badge tone="gray">{item.execution.replace('_', ' ')}</Badge>
                  </p>
                  <p className="mt-1 text-slate-600 dark:text-slate-300">
                    {item.requestNote}
                  </p>
                  {item.reviewNote && (
                    <p className="mt-1 text-xs text-slate-500">Review: {item.reviewNote}</p>
                  )}
                  {item.restorePointVerifiedAt && (
                    <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-400">
                      Restore point verified {timeLabel(item.restorePointVerifiedAt)}
                    </p>
                  )}
                </div>
                {data.permissions.reviewRemediation && item.status === 'requested' && (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => review.mutate({ id: item.id, decision: 'approve' })}
                    >
                      Approve record
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => review.mutate({ id: item.id, decision: 'reject' })}
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {pendingError && <ErrorNote error={pendingError} />}
    </div>
  );
}

export function MonitoringPage({ portal = false }: { portal?: boolean }) {
  const me = useMe();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const subscriptionId = searchParams.get('subscription') ?? '';
  const [tab, setTab] = useState<MonitoringTab>('uptime');
  const overview = useQuery({
    queryKey: ['monitoring', 'overview'],
    queryFn: () => api.get<MonitoringOverviewDto>('/api/monitoring/overview'),
    refetchInterval: 60_000,
  });
  useEffect(() => {
    if (!subscriptionId && overview.data?.subscriptions[0]) {
      setSearchParams({ subscription: overview.data.subscriptions[0].id }, { replace: true });
    }
  }, [overview.data, subscriptionId, setSearchParams]);
  const detail = useQuery({
    queryKey: ['monitoring', 'subscription', subscriptionId],
    queryFn: () =>
      api.get<MonitoringSubscriptionDto>(
        `/api/monitoring/subscriptions/${subscriptionId}`,
      ),
    enabled: Boolean(subscriptionId),
    refetchInterval: 60_000,
  });
  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: ['monitoring', 'subscription', subscriptionId],
    });
    void queryClient.invalidateQueries({ queryKey: ['monitoring', 'overview'] });
  };

  if (overview.isPending) return <Spinner />;
  if (overview.error) return <ErrorNote error={overview.error} />;
  const data = overview.data;
  return (
    <div>
      <PageHeader
        title="Monitoring & Security"
        description="Operational outcomes for uptime and workload risk—not a claim of Plesk Monitoring or Imunify product parity."
        actions={
          data.subscriptions.length > 1 ? (
            <Select
              aria-label="Subscription"
              value={subscriptionId}
              onChange={(event) => setSearchParams({ subscription: event.target.value })}
            >
              {data.subscriptions.map((subscription) => (
                <option key={subscription.id} value={subscription.id}>
                  {subscription.name} · {subscription.clientName}
                </option>
              ))}
            </Select>
          ) : undefined
        }
      />
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
        {[
          ['Checks', data.checks],
          ['Checks down', data.checksDown],
          ['Open incidents', data.openIncidents],
          ['Open findings', data.openFindings],
          ['Failed scans', data.failedScans],
        ].map(([label, value]) => (
          <Card key={label}>
            <div className="px-4 py-3">
              <p className="text-xs text-slate-500">{label}</p>
              <p className="mt-1 text-2xl font-semibold">{value}</p>
            </div>
          </Card>
        ))}
      </div>
      {data.subscriptions.length === 0 ? (
        <Card>
          <EmptyState
            title="No visible monitoring subscriptions"
            description={
              me.data?.role === 'client'
                ? 'Your plan must enable client monitoring visibility.'
                : 'Create a subscription and enable monitoring features on its plan.'
            }
          />
        </Card>
      ) : (
        <>
          <Tabs
            active={tab}
            onChange={(key) => setTab(key as MonitoringTab)}
            tabs={[
              { key: 'uptime', label: 'Uptime & incidents' },
              { key: 'security', label: 'Workload security' },
            ]}
          />
          {detail.isPending ? (
            <Spinner />
          ) : detail.error ? (
            <ErrorNote error={detail.error} />
          ) : tab === 'uptime' ? (
            <UptimePanel data={detail.data} refresh={refresh} selectedCheckId={searchParams.get('check')} />
          ) : (
            <SecurityPanel data={detail.data} refresh={refresh} portal={portal} />
          )}
        </>
      )}
    </div>
  );
}
